import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { NotebookPen } from 'lucide-react';
import { Typography } from 'antd';

import { formatDateTime } from '../../features/tasks/lib/taskMeta.js';
import { useNotes } from '../../features/notes/hooks/useNotes.js';
import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';

const { Text } = Typography;

export interface RecentNotesWidgetProps {
  className?: string;
  testId?: string;
  /** Показать первые N заметок из свежих. */
  limit?: number;
  onViewAll?: () => void;
}

/** Свежие заметки пространства — быстрый взгляд на то, что записали. */
export function RecentNotesWidget({
  className,
  testId,
  limit = 8,
  onViewAll,
}: RecentNotesWidgetProps): ReactElement {
  const { t } = useTranslation();
  const { data, isLoading } = useNotes();
  const notes = (data ?? []).slice(0, limit);

  const widgetContainerProps = {
    title: t('crm.dashboard.notes.title'),
    icon: NotebookPen,
    minHeight: 300,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
    headerRight: (
      <button
        type="button"
        onClick={onViewAll}
        className="border-0 bg-transparent text-xs"
        style={{ color: 'var(--ant-color-primary)', cursor: onViewAll ? 'pointer' : 'default' }}
      >
        {t('crm.dashboard.notes.view_all')}
      </button>
    ),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {isLoading ? (
        <div className="animate-pulse p-3.5 flex flex-col gap-1.5">
          {[1, 2, 3].map((index) => (
            <div
              key={index}
              className="crm-radius-s h-10"
              style={{ background: 'var(--g-fill, #f5f1eb)' }}
            />
          ))}
        </div>
      ) : notes.length === 0 ? (
        <div
          className="flex items-center justify-center gap-2"
          style={{ padding: '18px 16px', opacity: 0.6 }}
        >
          <NotebookPen size={16} style={{ color: 'var(--ant-color-text-tertiary)' }} />
          <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
            {t('crm.dashboard.notes.empty')}
          </span>
        </div>
      ) : (
        <ul className="m-0 list-none p-0">
          {notes.map((note) => (
            <li key={note.id}>
              <div
                className="w-full"
                style={{
                  padding: '8px 14px',
                  minHeight: 42,
                  borderBottom: '1px solid var(--ant-color-border-secondary)',
                }}
              >
                <Text
                  className="block whitespace-pre-wrap"
                  style={{
                    fontSize: 12.5,
                    color: 'var(--ant-color-text)',
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {note.body}
                </Text>
                <div className="mt-1 flex items-center gap-2">
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, color: 'var(--ant-color-text-secondary)' }}
                  >
                    {note.createdByMember?.user.name ?? t('crm.dashboard.notes.unknown_author')} ·{' '}
                    {formatDateTime(note.createdAt)}
                  </Text>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </WidgetContainer>
  );
}
